import React from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, FormControlLabel,
  Link, Stack, Switch, TextField, Typography,
} from "@mui/material";
import { tagManagerApi } from "../../../api/tagManager";
import { useAuth } from "../../../context/AuthContext";
import { hasProjectPermission } from "../../../utils/projectAccess";
import { isSuperAdmin } from "../../../utils/roles";

const EMPTY = { containerId: "", enabled: false, configured: false };
const VALID_ID = /^GTM-[A-Z0-9]{4,32}$/;

export default function TagManagerSettings({ projectId, project }) {
  const { user } = useAuth();
  const canManage = isSuperAdmin(user)
    || hasProjectPermission(project?.memberAccess, "project.settings.manage");
  const [saved, setSaved] = React.useState(EMPTY);
  const [form, setForm] = React.useState(EMPTY);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [loadError, setLoadError] = React.useState("");
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    tagManagerApi.get(projectId).then((response) => {
      if (!active) return;
      const config = response.googleTagManager || EMPTY;
      setSaved(config);
      setForm(config);
    }).catch((requestError) => {
      if (active) setLoadError(requestError.message || "No se pudo cargar Google Tag Manager.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [projectId, attempt]);

  const containerId = form.containerId.trim().toUpperCase();
  const invalidId = Boolean(containerId) && !VALID_ID.test(containerId);
  const dirty = containerId !== saved.containerId || form.enabled !== saved.enabled;
  const blocked = loading || busy || Boolean(loadError) || !canManage;

  const change = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError("");
    setMessage("");
  };

  const save = async (event) => {
    event.preventDefault();
    if (blocked || !dirty) return;
    if (!VALID_ID.test(containerId)) {
      setError("Ingresá el ID GTM- del contenedor. Si querés quitarlo, usá Eliminar conexión.");
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await tagManagerApi.save(projectId, { containerId, enabled: form.enabled });
      setSaved(response.googleTagManager);
      setForm(response.googleTagManager);
      setMessage("Configuración guardada. Se aplicará al abrir o recargar la web pública.");
    } catch (requestError) {
      setError(requestError.message || "No se pudo guardar la configuración.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (blocked || !confirmDelete || !saved.configured) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await tagManagerApi.remove(projectId);
      setSaved(response.googleTagManager || EMPTY);
      setForm(response.googleTagManager || EMPTY);
      setConfirmDelete(false);
      setMessage("Conexión eliminada. Se aplicará al abrir o recargar la web pública.");
    } catch (requestError) {
      setConfirmDelete(false);
      setError(requestError.message || "No se pudo eliminar la conexión.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Google Tag Manager</Typography>
              <Typography variant="body2" color="text.secondary">
                Conectá las etiquetas de medición de este negocio con su web pública.
              </Typography>
            </Box>
            <Chip sx={{ alignSelf: "flex-start" }} size="small"
              color={saved.enabled ? "success" : "default"}
              label={loading ? "Cargando…" : loadError ? "Sin consultar" : saved.enabled ? "Activado" : saved.configured ? "Desactivado" : "Sin configurar"} />
          </Stack>
          {loadError ? <Alert severity="error" action={<Button color="inherit" onClick={() => setAttempt((n) => n + 1)}>Reintentar</Button>}>{loadError}</Alert> : null}
          {!canManage ? <Alert severity="info">Podés consultar la conexión. Para modificarla necesitás permiso para editar la configuración del proyecto.</Alert> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {message ? <Alert severity="success">{message}</Alert> : null}
          <Box component="form" onSubmit={save}>
            <Stack spacing={2}>
              <TextField fullWidth label="ID del contenedor" placeholder="GTM-ABCDEFGH"
                value={form.containerId} disabled={blocked} error={invalidId}
                onChange={(event) => change("containerId", event.target.value.toUpperCase())}
                inputProps={{ maxLength: 36, autoCapitalize: "characters", spellCheck: false }}
                helperText={invalidId ? "Usá un ID GTM-. Los ID G- y AW- pertenecen a etiquetas, no a contenedores." : "Copiá sólo el ID GTM- de Google Tag Manager."} />
              <FormControlLabel control={<Switch checked={form.enabled} disabled={blocked}
                onChange={(event) => change("enabled", event.target.checked)} />}
                label="Activar Google Tag Manager en la web" />
              {dirty ? <Typography variant="body2" color="text.secondary">Tenés cambios sin guardar.</Typography> : null}
              {canManage ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button type="submit" variant="contained" disabled={blocked || !dirty || !containerId || invalidId}>
                    {busy ? "Guardando…" : "Guardar"}
                  </Button>
                  {dirty ? <Button disabled={blocked} onClick={() => { setForm(saved); setError(""); setMessage(""); }}>Descartar cambios</Button> : null}
                  {saved.configured ? <Button color="error" disabled={blocked} onClick={() => setConfirmDelete(true)}>Eliminar conexión</Button> : null}
                </Stack>
              ) : null}
            </Stack>
          </Box>
          <Divider />
          <Typography sx={{ fontWeight: 800 }}>Cómo conectarlo</Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { mb: 1 } }}>
            <Typography component="li" variant="body2">En <Link href="https://tagmanager.google.com/" target="_blank" rel="noopener noreferrer">Google Tag Manager</Link>, creá o abrí un contenedor de tipo Web para este negocio.</Typography>
            <Typography component="li" variant="body2">Copiá su ID GTM-, pegalo arriba, activá la conexión y guardá.</Typography>
            <Typography component="li" variant="body2">Configurá las etiquetas de Analytics, Google Ads u otros servicios en Tag Manager y publicá sus cambios con Enviar.</Typography>
            <Typography component="li" variant="body2">Abrí la web pública con Vista previa de Tag Manager y verificá qué etiquetas se disparan.</Typography>
          </Box>
          <Alert severity="info">
            Guardar el ID conecta el contenedor. Las etiquetas y conversiones se configuran en Google Tag Manager; los informes de esta sección siguen usando la actividad de WebProfit.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Para medir la navegación interna, la web emite el evento <strong>wp_page_view</strong> con <strong>page_path</strong> y <strong>page_title</strong>. Usalo como activador personalizado en GTM. Si lo usás para GA4, evitá sumar otro seguimiento automático de vistas de página.
          </Typography>
        </Stack>
      </CardContent>
      <Dialog open={confirmDelete} onClose={() => { if (!busy) setConfirmDelete(false); }}
        aria-labelledby="tag-manager-delete-title">
        <DialogTitle id="tag-manager-delete-title">Eliminar conexión con Google Tag Manager</DialogTitle>
        <DialogContent><DialogContentText>Se quitará {saved.containerId} de este proyecto. El contenedor y sus etiquetas seguirán existiendo en tu cuenta de Google.</DialogContentText></DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConfirmDelete(false)}>Cancelar</Button>
          <Button color="error" disabled={busy} onClick={remove}>{busy ? "Eliminando…" : "Eliminar conexión"}</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
